import { _throwNotImplementedException } from './COM';
import { $ } from '../../../vendor/jquery';
import _ from 'underscore';

/**
 * Holds a collection of vendor-specific Rs232DisplayAdapters
 * @type {object<string, Rs232DisplayAdapter>}
 * @private
 */
var _adapters = {};

/**
 * Generic Rs232DisplayAdapter
 * @class
 */
function Rs232DisplayAdapter() {
    this._noCommandsBefore = null;
}

/**
 * Queries the underlying RS232 connection for the connected device's model
 * @method
 * @returns {Promise.<Rs232Reply>}
 * @abstract
 */
Rs232DisplayAdapter.prototype.getModel = _throwNotImplementedException('getModel');

/**
 * Queries the underlying RS232 connection for the connected device's serial
 * number
 * @method
 * @returns {Promise.<Rs232Reply>}
 * @abstract
 */
Rs232DisplayAdapter.prototype.getSerialNumber = _throwNotImplementedException('getSerialNumber');

/**
 * Queries the underlying RS232 connection for the connected device's power
 * state
 * @method
 * @returns {Promise.<Rs232Reply>}
 * @abstract
 */
Rs232DisplayAdapter.prototype.getPowerState = _throwNotImplementedException('getPowerState');

/**
 * Sends an RS232 command to the underlying RS232 connection to set the
 * connected device's power state
 * @method
 * @param {Rs232PowerState} desiredState
 * @returns {Promise.<Rs232Reply>}
 * @abstract
 */
Rs232DisplayAdapter.prototype.setPowerState = _throwNotImplementedException('setPowerState');

/**
 * Queries the underlying RS232 connection for the connected device's mute state
 * @method
 * @returns {Promise.<Rs232Reply>}
 * @abstract
 */
Rs232DisplayAdapter.prototype.getMuted = _throwNotImplementedException('getMuted');

/**
 * Sends an RS232 command to the underlying RS232 connection to set the
 * connected device's mute state
 * @method
 * @param {Rs232MuteState} desiredState
 * @returns {Promise.<Rs232Reply>}
 * @abstract
 */
Rs232DisplayAdapter.prototype.setMuted = _throwNotImplementedException('setMuted');

/**
 * Queries the underlying RS232 connection for the connected device's audio
 * volume
 * @method
 * @returns {Promise.<Rs232Reply>}
 * @abstract
 */
Rs232DisplayAdapter.prototype.getAudioVolume = _throwNotImplementedException('getAudioVolume');

/**
 * Sends an RS232 command to the underlying RS232 connection to set the
 * connected device's audio volume
 * @method
 * @param {number} desiredVolume
 * @returns {Promise.<Rs232Reply>}
 * @abstract
 */
Rs232DisplayAdapter.prototype.setAudioVolume = _throwNotImplementedException('setAudioVolume');

/**
 * Queries the underlying RS232 connection for the connected device's selected
 * audio/video input
 * @method
 * @returns {Promise.<Rs232Reply>}
 * @abstract
 */
Rs232DisplayAdapter.prototype.getVideoInput = _throwNotImplementedException('getVideoInput');

/**
 * Sends an RS232 command to the underlying RS232 connection to set the
 * connected device's selected video input.
 *
 * NOTE: different make/model support different inputs. The Rs232VideoInput
 *       enumeration describes common inputs, however, documentation for a
 *       specific vendor should be consulted to see true options for that
 *       vendor.
 * @method
 * @param {Rs232VideoInput} desiredInput
 * @returns {Promise.<Rs232Reply>}
 * @abstract
 */
Rs232DisplayAdapter.prototype.setVideoInput = _throwNotImplementedException('setVideoInput');

/**
 * Gets the time until the command is done executing (0 means no commands
 * currently executing)
 * @return {number}
 */
Rs232DisplayAdapter.prototype.getRemainingRefractoryPeriod = function () {
    if (this._noCommandsBefore && Date.now() < this._noCommandsBefore) {
        return Date.now() - this._noCommandsBefore;
    }

    return 0;
};

/**
 * The promise will resolve when the current executing command is finished
 * (resolves immediately if none is running). Use setRefractoryPeriod to set the
 * amount of time a command needs to execute (even though the command may get a
 * response from the COM device)
 * @return $.Deferred
 */
Rs232DisplayAdapter.prototype.waitForIdle = function () {
    var jqDeferred = $.Deferred();

    if (this._noCommandsBefore && Date.now() < this._noCommandsBefore) {
        var timeout = this._noCommandsBefore - Date.now();
        setTimeout(function () {
            this._noCommandsBefore = null;
            jqDeferred.resolve();
        }, timeout);
    } else {
        jqDeferred.resolve();
    }

    return jqDeferred.promise();
};

/**
 * This command sets the amount of time a command needs to execute (even though
 * the command may get a response from the COM device)
 * @param msInTheFuture Specifies the number of ms in the future that current
 * command will be finished executing
 */
Rs232DisplayAdapter.prototype.setRefractoryPeriod = function (msInTheFuture) {
    this._noCommandsBefore = Date.now() + msInTheFuture;
};

/**
 * Creates a concrete display adapter for a given make and model of display
 * @param {string} displayMake - the vendor name of the hardware display
 * @param {string} displayModel - the model name of the hardware display
 * @param {Rs232Controller} rs232Controller - controller for interacting with
 * COM port
 * @returns {Rs232DisplayAdapter}
 */
Rs232DisplayAdapter.createVendorAdapter = function (displayMake, displayModel, rs232Controller) {
    return new _adapters[displayMake.toLowerCase()](displayMake, displayModel, rs232Controller);
};

/**
 * Registers a concrete vendor display adapter class with Rs232DisplayAdapter.
 * This method is used by vendor specific adapters to make Rs232DisplayAdapter
 * aware of the vendor and should not be used by end-devs.
 * @param {string} displayMake - the vendor name of the hardware display
 * @param {Rs232DisplayAdapter} rs232DisplayAdapter - the model name of the
 * hardware display
 */
Rs232DisplayAdapter.registerVendorAdapter = function (displayMake, rs232DisplayAdapter) {
    _adapters[displayMake.toLowerCase()] = rs232DisplayAdapter;
};

/**
 * Returns a list of vendors for which adapters have been registered.
 * @returns {string[]}
 */
Rs232DisplayAdapter.enumerateVendorNames = function () {
    return _.keys(_adapters);
};

export { Rs232DisplayAdapter };
